import { useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { getCpPayoutBalance } from '../api/cpPayout';

function getKstDateParts(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  return Object.fromEntries(parts.map((part) => [part.type, part.value]));
}

function getInitialDateRange() {
  const { year, month, day } = getKstDateParts();
  return {
    startDate: `${year}-${month}-01`,
    endDate: `${year}-${month}-${day}`,
  };
}

function toApiDate(value) {
  return String(value || '').replaceAll('-', '');
}

function isValidDateInput(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

function formatDate(value) {
  const raw = String(value || '').replace(/\D/g, '');
  if (!/^\d{8}$/.test(raw)) return '-';
  return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
}

function formatKstDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
}

function formatAmount(value) {
  const raw = String(value ?? '').trim();
  if (!/^-?\d+$/.test(raw)) return '조회 불가';
  try {
    return `${window.BigInt(raw).toLocaleString('ko-KR')}원`;
  } catch (_) {
    return '조회 불가';
  }
}

function normalizeBalance(value, requestedStartDate, requestedEndDate) {
  const balance = value?.balance ?? value ?? {};
  const transactionPeriod = String(balance.transactionPeriod ?? balance.transaction_period ?? '').replace(/\D/g, '');
  return {
    provider: String(balance.provider || 'KG_INICIS'),
    code: String(balance.code || ''),
    message: String(balance.message || ''),
    totalAmount: balance.totalAmount ?? balance.total_amount,
    startDate: String((balance.startDate ?? balance.start_date) || transactionPeriod.slice(0, 8) || requestedStartDate),
    endDate: String((balance.endDate ?? balance.end_date) || transactionPeriod.slice(8, 16) || requestedEndDate),
    requestId: String(balance.requestId ?? balance.request_id ?? ''),
  };
}

function balanceErrorMessage(error) {
  const code = Number(error?.code ?? error?.response?.data?.code);
  const status = Number(error?.response?.status);
  const detail = String(error?.message ?? error?.response?.data?.msg ?? '').trim();
  const suffix = detail ? `\n상세: ${detail.length > 300 ? `${detail.slice(0, 300)}…` : detail}` : '';
  if (code === 4000 || status === 400) return `입력한 조회기간을 확인해 주세요.${suffix}`;
  if (code === 5020 || status === 502) return `KG이니시스 잔액조회에 실패했습니다.${suffix}`;
  if (code === 9999 || status === 401 || status === 403) return detail || '로그인 상태와 SUPER 관리자 권한을 확인해 주세요.';
  return detail || 'KG이니시스 잔액조회에 실패했습니다.';
}

function isBalanceNoData(error) {
  const detail = String(error?.message ?? error?.response?.data?.msg ?? '').toUpperCase();
  return detail.includes('SD02') || detail.includes('잔액대사 내역 데이터가 없습니다');
}

export default function PayoutBalanceSection({ capability, compact = false }) {
  const initialRangeRef = useRef(null);
  if (!initialRangeRef.current) initialRangeRef.current = getInitialDateRange();
  const requestRef = useRef(false);
  const [startDate, setStartDate] = useState(initialRangeRef.current.startDate);
  const [endDate, setEndDate] = useState(initialRangeRef.current.endDate);
  const [balance, setBalance] = useState(null);
  const [noData, setNoData] = useState(false);
  const [lastCheckedAt, setLastCheckedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const enabled = capability?.enabled === true && capability?.balanceCheckEnabled === true;
  const maxDate = initialRangeRef.current.endDate;

  const validate = () => {
    if (!startDate || !endDate) return '조회 시작일과 종료일을 모두 선택해 주세요.';
    if (!isValidDateInput(startDate) || !isValidDateInput(endDate)) return '유효한 조회일자를 선택해 주세요.';
    if (startDate > endDate) return '조회 시작일은 종료일보다 늦을 수 없습니다.';
    if (startDate > maxDate || endDate > maxDate) return '미래 날짜는 조회할 수 없습니다.';
    return '';
  };

  const lookupBalance = async () => {
    if (!enabled || requestRef.current) return;
    const validationError = validate();
    setError(validationError);
    if (validationError) return;

    const requestedStartDate = toApiDate(startDate);
    const requestedEndDate = toApiDate(endDate);
    requestRef.current = true;
    setLoading(true);
    setBalance(null);
    setNoData(false);
    try {
      const data = await getCpPayoutBalance({ startDate: requestedStartDate, endDate: requestedEndDate });
      setBalance(normalizeBalance(data, requestedStartDate, requestedEndDate));
      setNoData(false);
      setLastCheckedAt(new Date().toISOString());
      setError('');
    } catch (requestError) {
      if (isBalanceNoData(requestError)) {
        setBalance({
          provider: 'KG_INICIS',
          code: 'SD02',
          message: '조회기간에 잔액대사 내역이 없습니다.',
          totalAmount: null,
          startDate: requestedStartDate,
          endDate: requestedEndDate,
          requestId: '',
        });
        setNoData(true);
        setLastCheckedAt(new Date().toISOString());
        setError('');
      } else {
        setError(balanceErrorMessage(requestError));
      }
    } finally {
      requestRef.current = false;
      setLoading(false);
    }
  };

  return (
    <section className={(compact ? 'mt-5 border-t border-[#e5e8eb] pt-5' : '') + ' space-y-4'}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className={(compact ? 'text-[16px]' : 'text-[20px]') + ' font-bold text-[#191f28]'}>KG이니시스 지급대행 잔액조회</h2>
            <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700">조회 전용</span>
          </div>
          <p className="mt-1 text-[12px] leading-5 text-[#6b7684]">지급대행 MID의 현재 지급 가능 잔액을 조회합니다.</p>
        </div>
      </div>

      <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-3 text-[12px] leading-5 text-blue-900">
        잔액은 KG이니시스 지급대행 MID 기준입니다. 동일 MID를 사용하는 서비스의 지급 금액이 합산되어 표시될 수 있습니다.
      </div>

      {!enabled && (
        <div role="status" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-[12px] leading-5 text-amber-900">
          현재 서버에서 KG이니시스 지급대행 잔액조회를 지원하지 않습니다.
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="text-[12px] font-semibold text-[#6b7684]">조회 시작일
          <input type="date" required max={maxDate} disabled={!enabled || loading} value={startDate} onChange={(event) => { setStartDate(event.target.value); setError(''); }} className="mt-1 h-[42px] w-full rounded-lg border border-[#dfe3e8] bg-white px-3 text-[13px] text-[#191f28] outline-none focus:border-[var(--brand-primary)] disabled:bg-[#f2f4f6]" />
        </label>
        <label className="text-[12px] font-semibold text-[#6b7684]">조회 종료일
          <input type="date" required max={maxDate} disabled={!enabled || loading} value={endDate} onChange={(event) => { setEndDate(event.target.value); setError(''); }} className="mt-1 h-[42px] w-full rounded-lg border border-[#dfe3e8] bg-white px-3 text-[13px] text-[#191f28] outline-none focus:border-[var(--brand-primary)] disabled:bg-[#f2f4f6]" />
        </label>
      </div>

      <button type="button" disabled={!enabled || loading} onClick={lookupBalance} className="flex h-[42px] w-full items-center justify-center gap-2 rounded-lg bg-[var(--brand-primary)] px-4 text-[13px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-40">
        <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
        {loading ? '잔액 조회 중...' : balance ? '잔액 다시 조회' : '잔액조회'}
      </button>

      {error && <div role="alert" className="whitespace-pre-line rounded-xl border border-red-200 bg-red-50 p-3 text-[12px] leading-5 text-red-800">{error}</div>}
      {noData && <div role="status" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-[12px] leading-5 text-amber-900">선택한 조회기간에 잔액대사 내역이 없습니다. 조회기간을 변경해 다시 조회해 주세요.</div>}

      <div aria-live="polite" className="rounded-xl border border-[#e5e8eb] bg-white p-4">
        <p className="text-[11px] font-semibold text-[#8b95a1]">KG이니시스 지급 가능 잔액</p>
        <p className="mt-1 break-all text-[24px] font-extrabold tabular-nums text-[#191f28]">{balance ? (noData ? '조회 내역 없음' : formatAmount(balance.totalAmount)) : '-'}</p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <BalanceDetail label="조회기간" value={balance ? `${formatDate(balance.startDate)} ~ ${formatDate(balance.endDate)}` : '-'} />
          <BalanceDetail label="마지막 조회" value={formatKstDateTime(lastCheckedAt)} />
        </div>
        {balance && (balance.code || balance.message) && <p className="mt-3 text-[11px] text-[#8b95a1]">조회 결과: {[balance.code, balance.message].filter(Boolean).join(' · ')}</p>}
        {balance?.requestId && <p className="mt-1 break-all font-mono text-[10px] text-[#adb5bd]">요청 ID: {balance.requestId}</p>}
      </div>
    </section>
  );
}

function BalanceDetail({ label, value }) {
  return <div><p className="text-[11px] font-semibold text-[#8b95a1]">{label}</p><p className="mt-1 text-[12px] font-semibold text-[#333d4b]">{value}</p></div>;
}
